
import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, FileText, BarChart, Lightbulb, ArrowRight, Star, Lock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

const ProductCopilotPreview: React.FC = () => {
  const { user } = useAuth();

  const features = [
    {
      icon: FileText,
      title: "PRD Generation",
      description: "Create comprehensive Product Requirements Documents in minutes",
      color: "text-blue-600"
    },
    {
      icon: BarChart,
      title: "Market Research",
      description: "Get instant insights on market trends and competitor analysis",
      color: "text-green-600"
    },
    {
      icon: Lightbulb,
      title: "Feature Brainstorming",
      description: "Generate innovative feature ideas with AI-powered creativity",
      color: "text-amber-600"
    }
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary/5 to-secondary/10">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <Badge className="mb-4 px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 border-purple-200">
            <Star className="h-4 w-4 mr-2" />
            Premium Feature
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Meet Your AI Product Manager
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Product Copilot replaces $2000+/month consultants. Create professional PRDs, 
            roadmaps, and strategic documents in minutes, not hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Demo Visual */}
          <div className="space-y-6">
            <Card className="relative overflow-hidden border-2 border-primary/20">
              <div className="absolute top-4 right-4">
                <Badge variant="secondary" className="bg-primary/10 text-primary">
                  <Sparkles className="h-3 w-3 mr-1" />
                  AI Powered
                </Badge>
              </div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Product Requirements Document
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-4 rounded-lg text-sm font-mono mb-4">
                  <div className="text-gray-600">## Problem Statement</div>
                  <div className="text-gray-800">Our users need a faster way to...</div>
                  <div className="text-gray-600 mt-2">## Success Metrics</div>
                  <div className="text-gray-800">• 50% reduction in task completion time</div>
                  <div className="text-gray-800">• 90% user satisfaction score</div>
                </div>
                <div className="flex justify-between items-center text-sm text-muted-foreground">
                  <span>Generated in 2 minutes</span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Professional quality
                  </span>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-3 gap-4">
              {features.map((feature, index) => (
                <Card key={index} className="text-center p-4">
                  <feature.icon className={`h-8 w-8 ${feature.color} mx-auto mb-2`} />
                  <h3 className="font-medium text-sm mb-1">{feature.title}</h3>
                  <p className="text-xs text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Benefits & CTA */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 rounded-full p-2 mt-1">
                  <ArrowRight className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Save 10+ Hours Per Week</h3>
                  <p className="text-muted-foreground text-sm">
                    Automate document creation that typically takes days
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-blue-100 rounded-full p-2 mt-1">
                  <BarChart className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Professional Quality Output</h3>
                  <p className="text-muted-foreground text-sm">
                    Enterprise-grade templates used by Fortune 500 companies
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-purple-100 rounded-full p-2 mt-1">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">AI-Powered Intelligence</h3>
                  <p className="text-muted-foreground text-sm">
                    Context-aware suggestions and industry best practices
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg border border-purple-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-800 mb-1">$1,300/month</div>
                <div className="text-sm text-purple-600 mb-3">Average time savings value</div>
                <div className="text-xs text-muted-foreground">
                  Based on 5 hours saved per week at $65/hour
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {user ? (
                <Button asChild size="lg" className="w-full">
                  <Link to="/pricing" className="flex items-center justify-center gap-2">
                    <Star className="h-4 w-4" />
                    Upgrade to Pro - $10/month
                  </Link>
                </Button>
              ) : (
                <Button asChild size="lg" className="w-full">
                  <Link to="/sign-up" className="flex items-center justify-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Start Free Trial
                  </Link>
                </Button>
              )}
              
              <Button asChild variant="outline" size="lg" className="w-full">
                <Link to="/product-copilot" className="flex items-center justify-center gap-2">
                  <Lock className="h-4 w-4" />
                  Preview Product Copilot
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductCopilotPreview;
